<template>
  <page layoutClass="projects-index" :loading="!mentorship || isLoading">
    <!-- Mentees -->
    <template v-if="user.mentorStatus && mentorship">
      <b-row class="p-3">
        <b-col cols="12" sm="12">
          <h3 class="m-0 float-left">{{ isCXR ? 'Colleagues Connecting with You' : 'Your Mentees' }}</h3>
          <b-button
            variant="outline-primary"
            class="float-right"
            v-if="user.mentorStatus !== 'INACTIVE'"
            @click="$bvModal.show('update-status-modal')"
          >Edit Status</b-button>
        </b-col>
      </b-row>
      <b-row class="p-3" v-if="user.mentorStatus === 'REQUESTED'">
        <b-col>
          <b-card class="d-flex justify-content-center align-items-center p-5">
            <div class="text-center">You have requested to be a mentor. Please wait until an administrator accepts your request.</div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="p-3" v-else-if="user.mentorStatus === 'REJECTED'">
        <b-col>
          <b-card class="d-flex justify-content-center align-items-center p-5">
            <div class="text-center">Your request to be a mentor was rejected. Please contact with an administrator if you think it is a mistake.</div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="p-3" v-else-if="user.mentorStatus === 'HIDDEN'">
        <b-col>
          <b-card class="d-flex justify-content-center align-items-center p-5">
            <div class="text-center">Your profile is hidden. To activate your profile click on the 'Update Status' button.</div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="p-3" v-else-if="user.mentorStatus === 'INACTIVE'">
        <b-col>
          <b-card class="d-flex justify-content-center align-items-center p-5">
            <div class="text-center">Your status is inactive. To activate your profile contact with an admin.</div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="p-3" v-else-if="user.mentorStatus === 'ACTIVE' && (!Array.isArray(mentorship.menteeRequests) || !mentorship.menteeRequests.length)">
        <b-col>
          <b-card class="d-flex justify-content-center align-items-center p-5">
            <div class="text-center">You don't have any mentor requests yet</div>
          </b-card>
        </b-col>
      </b-row>
      <b-row class="px-3" v-else-if="user.mentorStatus === 'ACTIVE'">
        <b-col
          cols="12"
          lg="4"
          class="pb-4 h-100"
          v-for="menteeRequest in mentorship.menteeRequests"
          :key="menteeRequest.id"
        >
        <b-card class="h-100">
            <mentee-request-card
              :mentorRequest="menteeRequest"
              :company="company"
              @acceptMentorRequest="acceptMentorRequest($event)"
              @rejectMentorRequest="rejectMentorRequest($event)"
              @selectContact="selectContact($event)"
            />
          </b-card>
        </b-col>
      </b-row>
    </template>

    <!-- Mentors -->
    <template v-if="mentorship">
      <b-row class="p-3 mt-3">
        <b-col cols="12" sm="12">
          <h3 class="m-0 float-left">{{ isCXR ? 'Colleagues You Have Connected With' : 'Your Mentors' }}</h3>
        </b-col>
      </b-row>
      <b-row class="p-3" v-if="Array.isArray(mentorship.mentorRequests) && mentorship.mentorRequests.length">
        <b-col
          cols="12"
          lg="4"
          class="pb-4 h-100"
          v-for="mentorRequest in mentorship.mentorRequests"
          :key="mentorRequest.id"
        >
        <b-card class="h-100">
            <mentor-request-card
              :mentorRequest="mentorRequest"
              :company="company"
              @acceptMentorRequest="acceptMentorRequest($event)"
              @rejectMentorRequest="rejectMentorRequest($event)"
              @selectContact="selectContact($event)"
            />
          </b-card>
        </b-col>
        <b-col cols="12" lg="4" class="pb-4">
          <b-card class="h-100" body-class="d-flex align-items-center justify-content-center text-center">
            <div>
              <div>Are you looking for more mentors?</div>
              <div><b-link :to="{ name: 'MentorsIndex' }">See all mentors</b-link></div>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-else class="p-3">
        <b-col>
          <b-card class="p-5 mb-3 d-flex align-items-center justify-content-center text-center">
            <div>You have no mentors yet. Are you looking for one?</div>
            <div><b-link :to="{ name: 'MentorsIndex' }">See all mentors</b-link></div>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <b-modal
      v-if="user.mentorStatus"
      id="update-status-modal"
      size="sm"
      title="Update Mentor Status"
      hide-footer
    >
      <update-mentor-status-form
        :value="user.mentorStatus"
        role="mentor"
        @input="updateMentorStatus($event)"
      />
    </b-modal>

    <!-- Mentor Contact Modal -->
    <b-modal
      v-if="selectedContact"
      id="profile-contact-modal"
      :title="`Contact ${selectedContact ? selectedContact.fullName : ''}`"
      body-class="gray-bg"
      hide-footer
      lazy
    >
      <b-form-group label-for="subject">
        <h4><b>From Name</b></h4>
        <b-form-input type="text" v-model="contactForm.fromName" disabled></b-form-input>
      </b-form-group>
      <b-form-group label-for="subject">
        <h4><b>Subject</b></h4>
        <b-form-input type="text" v-model="contactForm.subject"></b-form-input>
      </b-form-group>
      <b-form-group>
        <h4><b>Message</b></h4>
        <p class="text-muted">This text will be sent to {{ selectedContact ? selectedContact.fullName : '' }} as an email from the platform.</p>
        <tip-tap-editor ref="tipTapEditor" :value="contactForm.message" @input="value => contactForm.message = value"/>
      </b-form-group>
      <b-button
        variant="primary"
        class="float-right"
        @click="sendEmail"
      >
        Send Email
      </b-button>
    </b-modal>

    <b-modal ref="testEmailSentSucessfullyModal" id="testEmailSentSucessfullyModal" title="Test Email Sent Successfully" body-class="py-5" hide-header>
      <p class="feedback-icon-line success"><font-awesome-icon icon="check"/></p>
      <p class="text-dark feedback-text-line">The email has been successfully sent.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('testEmailSentSucessfullyModal')">OK</b-button>
      </template>
    </b-modal>

    <b-modal ref="emailSendErrorModal" id="emailSendErrorModal" title="Error Sending Emails" body-class="py-5" hide-header>
      <p class="feedback-icon-line error"><font-awesome-icon icon="times"/></p>
      <p class="text-dark feedback-text-line">Unfortunately, we coud not send your email.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('emailSendErrorModal')">OK</b-button>
      </template>
    </b-modal>
  </page>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import Page from '@/components/Page';
import CurrentUserMentorshipQuery from '@/graphql/queries/CurrentUserMentorship.gql';
import ApplicationCard from '@/components/profile/ApplicationCard.vue';
import UpdateMentorRequestMutation from '@/graphql/mutations/UpdateMentorRequest.gql';
import UpdateMentorStatusMutation from '@/graphql/mutations/UpdateMentorStatus.gql';
import UpdateMentorStatusForm from '@/components/mentors/UpdateMentorStatusForm';
import MentorRequestCard from '@/components/profile/MentorRequestCard.vue';
import MenteeRequestCard from '@/components/profile/MenteeRequestCard.vue';
import TipTapEditor from '@/components/misc/TiptapEditor';
import SendEmailMutation from '@/graphql/mutations/SendEmail.gql';

const emailQuery = /* GraphQL */`
  query CustomTemplateQuery {
    company {
      id
      name
      domain(where: { primary: true }) {
        domain(prefix:"https://")
        emailFrom
      }
      branding {
        name
        bigLogo {
          id
          fileLocation
        }
        smallLogo {
          id
          fileLocation
        }
        emailHeaderImage {
          id
          fileLocation
        }
        headerFont
        bodyFont
        primaryColour
        secondaryColour
        backgroundColour
      }
    }
    user {
      id
      firstName
      fullName
      emailAddress
      title
    }
  }
`;

export default {
  components: {
    Page,
    ApplicationCard,
    UpdateMentorStatusForm,
    MentorRequestCard,
    MenteeRequestCard,
    TipTapEditor,
  },
  data() {
    return {
      selectedContact: null,
      contactForm: {
        subject: '',
        message: '',
        fromName: '',
      },
      isLoading: false
    };
  },
  apollo: {
    mentorship: {
      query: CurrentUserMentorshipQuery,
      update({ user }) {
        if (user) {
          const { menteeRequests, mentorRequests } = user;
          return { menteeRequests, mentorRequests };
        }
        return {};
      },
      result() {
        if (this.mentorship && this.$route.query && this.$route.query.mentorRequestID) {
          const { menteeRequests, mentorRequests } = this.mentorship;
          const requests = [ ...(menteeRequests || []), ...(mentorRequests || []) ];
          const request = requests.find(el => el.id === this.$route.query.mentorRequestID);
          if (request) {
            setTimeout(() => this.$bvModal.show(`mentor-request-${request.id}`), 500);
          }
        }
      },
      error(err) {
        this.$notifyFatalErr(err);
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isCXR' ]),
  },
  methods: {
    async acceptMentorRequest({ mentorRequest }) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: UpdateMentorRequestMutation,
          variables: {
            mentorRequestID: mentorRequest.id,
            update: {
              status: 'ACTIVE',
            },
          },
          update(store, { data: { updateMentorRequest } }) {
            const data = store.readQuery({ query: CurrentUserMentorshipQuery });
            data.user.menteeRequests = data.user.menteeRequests.map(el => {
              if (el.id === updateMentorRequest.id) {
                el = updateMentorRequest;
              }
              return el;
            });
            store.writeQuery({ query: CurrentUserMentorshipQuery, data });
          },
        });
        console.log('SUCCESS: ', data);
        Promise.resolve(this.$root.captureLog({
          eventName: 'IM_MENTOR_REQUEST_ACCEPTED',
          user: { id: this.user.id },
          data: [ { key: 'mentorRequestID', value: mentorRequest.id } ],
        }));
      } catch (error) {
        console.error(error);
      }
    },
    async rejectMentorRequest({ mentorRequest }) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: UpdateMentorRequestMutation,
          variables: {
            mentorRequestID: mentorRequest.id,
            update: {
              status: 'REJECTED',
            },
          },
          update(store, { data: { updateMentorRequest } }) {
            const data = store.readQuery({ query: CurrentUserMentorshipQuery });
            data.user.menteeRequests = data.user.menteeRequests.map(el => {
              if (el.id === updateMentorRequest.id) {
                el = updateMentorRequest;
              }
              return el;
            });
            store.writeQuery({ query: CurrentUserMentorshipQuery, data });
          },
        });
        console.log('SUCCESS: ', data);
        Promise.resolve(this.$root.captureLog({
          eventName: 'IM_MENTOR_REQUEST_REJECTED',
          user: { id: this.user.id },
          data: [ { key: 'mentorRequestID', value: mentorRequest.id } ],
        }));
      } catch (error) {
        console.error(error);
      }
    },
    async updateMentorStatus(status) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: UpdateMentorStatusMutation,
          variables: {
            userID: this.user.id,
            mentorStatus: status,
          },
          update(store, { data: { updateUser } }) {
            const data = store.readQuery({ query: CurrentUserMentorshipQuery });
            data.user.mentorStatus = updateUser.mentorStatus;
            store.writeQuery({ query: CurrentUserMentorshipQuery, data });
          },
        });
        console.log('SUCCESS: ', data);
        Promise.resolve(this.$root.captureLog({
          eventName: 'IM_MENTOR_STATUS_UPDATED',
          user: { id: this.user.id },
        }));
      } catch (error) {
        console.error(error);
      }
      this.$bvModal.hide('update-status-modal');
    },
    selectContact(contact) {
      console.log(this.contactForm);
      this.selectedContact = contact;
      this.contactForm.subject = `Your Mentorship with ${this.user.fullName}`;
      this.contactForm.fromName = this.user.fullName;
      setTimeout(() => {
        this.$bvModal.show('profile-contact-modal');
      }, 100);
    },
    /* sendEmail() {
      console.log(this.contactForm);
      this.$bvModal.hide('profile-contact-modal');
    }, */
    async sendEmail() {
      console.log(this.contactForm);
      try {
        this.isLoading = true;
        const { id: userID } = this.selectedContact;
        const { contactForm, user } = this;

        // Manually check we have a userID to send to, otherwise we risk sending to everyone
        this.$assert(userID, new Error('Missing userID'));

        const email = {
          template: 'generic/html',
          data: JSON.stringify({
            title: contactForm.subject,
            body: `${contactForm.message}<hr/><p>${user.fullName}<br/>${user.emailAddress}</p>`,
          }),
          query: emailQuery,
          replyTo: user.emailAddress,
          where: { users: { id: userID } },
        };

        console.log(email);

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });
        this.isLoading = false;
        this.$bvModal.hide('profile-contact-modal');
        setTimeout(() => this.$refs.testEmailSentSucessfullyModal.show(), 300);
      } catch (err) {
        this.$log.error(err);
        this.isLoading = false;
        setTimeout(() => this.$refs.emailSendErrorModal.show(), 300);
      }
    },
  }
};
</script>

<style lang="scss">
.feedback-icon-line {
  font-size: 60px;
  text-align: center;

  &.success {
    color: #27ae60;
  }

  &.error {
    color: #e74c3c;
  }
}

.feedback-text-line {
  font-size: 14px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/views/User/Mentorship.vue